import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import type { Artist } from "@/types/models"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Week calculation utilities for Flash of the Week
// Returns ISO-8601 week number where weeks start on Monday and the first week of
// the year is the one that contains the first Thursday.  This avoids the bug
// where the previous implementation switched to the next week already on
// Saturday.  Source algorithm: https://stackoverflow.com/a/6117889/9953597
export function getWeekNumber(date: Date): number {
  // ISO week date weeks start on Monday, so correct the day number (Monday = 0 … Sunday = 6)
  const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  const dayNr = (target.getUTCDay() + 6) % 7

  // Set the target to the Thursday of the current week so that week 1 always
  // contains Jan-04 which is guaranteed to be in week 1 according to ISO-8601.
  target.setUTCDate(target.getUTCDate() - dayNr + 3)

  // January 4th is always in week 1 (ISO-8601)
  const firstThursday = new Date(Date.UTC(target.getUTCFullYear(), 0, 4))

  // Calculate full weeks to the target date
  const diff = target.getTime() - firstThursday.getTime()
  return 1 + Math.round(diff / 604800000) // 604800000 = 7 * 24 * 3600 * 1000
}

export function getCurrentWeekIndex(): number {
  // For Amsterdam FOTW, return week 24 (hardcoded for now)
  return 24
}

export function getTimeLeftInWeek() {
  const now = new Date()
  // Convert to Amsterdam time
  const amsterdamTime = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Amsterdam' }))
  const currentDay = amsterdamTime.getDay() // 0 = Sunday, 1 = Monday, etc.
  const currentHour = amsterdamTime.getHours()
  const currentMinute = amsterdamTime.getMinutes()
  
  // End time is Sunday at 21:00 (9 PM)
  const END_HOUR = 21
  
  // If it's past Sunday 9 PM Amsterdam time, return 0s to indicate voting has ended
  if (currentDay === 0 && currentHour >= END_HOUR) {
    return { days: 0, hours: 0, minutes: 0 }
  }
  
  // Calculate time until Sunday 9 PM
  let daysLeft = 7 - currentDay
  if (currentDay === 0) daysLeft = 0 // If it's Sunday, don't add extra days
  
  let hoursLeft = END_HOUR - currentHour - 1 // -1 because we're counting the current hour
  if (hoursLeft < 0) hoursLeft = END_HOUR - currentHour + 23 // Wrap around to next day
  
  const minutesLeft = 60 - currentMinute
  
  return { days: daysLeft, hours: hoursLeft, minutes: minutesLeft }
}

export function getWeekProgress(): number {
  const now = new Date()
  // Convert to Amsterdam time
  const amsterdamTime = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Amsterdam' }))
  const currentDay = amsterdamTime.getDay() // 0 = Sunday, 1 = Monday, etc.
  const currentHour = amsterdamTime.getHours()
  const currentMinute = amsterdamTime.getMinutes()
  
  const END_HOUR = 21 // 9 PM
  
  // Make Sunday = 7 for progress calculation
  const dayOfWeek = currentDay === 0 ? 7 : currentDay
  
  // Calculate progress including hours and minutes
  const totalMinutesInWeek = (6 * 24 * 60) + (END_HOUR * 60) // Minutes until Sunday 9 PM
  const minutesElapsed = ((dayOfWeek - 1) * 24 * 60) + (currentHour * 60) + currentMinute
  
  // Cap progress at 100%
  return Math.min((minutesElapsed / totalMinutesInWeek) * 100, 100)
}

/**
 * Check if the leaderboard should be visible
 * Leaderboard is visible in the last 2 days of the week (Friday 9pm onwards)
 */
export function isLeaderboardVisible(): boolean {
  const now = new Date()
  // Convert to Amsterdam time
  const amsterdamTime = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Amsterdam' }))
  const currentDay = amsterdamTime.getDay() // 0 = Sunday, 1 = Monday, etc.
  const currentHour = amsterdamTime.getHours()
  
  const LEADERBOARD_START_HOUR = 21 // 9 PM
  
  // Friday = 5, Saturday = 6, Sunday = 0
  // Leaderboard is visible from Friday 9pm until Sunday 9pm
  if (currentDay === 5 && currentHour >= LEADERBOARD_START_HOUR) {
    return true // Friday after 9pm
  }
  if (currentDay === 6) {
    return true // All day Saturday
  }
  if (currentDay === 0 && currentHour < LEADERBOARD_START_HOUR) {
    return true // Sunday before 9pm
  }
  
  return false
}

/**
 * Check if voting period has ended
 * Voting ends on Sunday at 21:00 (9 PM) Amsterdam time
 */
export function isVotingEnded(): boolean {
  // TEMPORARY: Force voting ended state for testing
  return true
  
  // const now = new Date()
  // // Convert to Amsterdam time
  // const amsterdamTime = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Amsterdam' }))
  // const currentDay = amsterdamTime.getDay() // 0 = Sunday, 1 = Monday, etc.
  // const currentHour = amsterdamTime.getHours()
  // 
  // const END_HOUR = 21 // 9 PM
  // 
  // // If it's Sunday 9 PM or later, voting has ended
  // if (currentDay === 0 && currentHour >= END_HOUR) {
  //   return true
  // }
  // 
  // return false
}

// Browser detection utilities
export function detectBrowser(): 'chrome' | 'safari' | 'firefox' | 'edge' | 'other' {
  if (typeof window === 'undefined') return 'other'
  
  const userAgent = navigator.userAgent.toLowerCase()
  
  if (userAgent.includes('chrome') && !userAgent.includes('edg')) {
    return 'chrome'
  } else if (userAgent.includes('safari') && !userAgent.includes('chrome')) {
    return 'safari'
  } else if (userAgent.includes('firefox')) {
    return 'firefox'
  } else if (userAgent.includes('edg')) {
    return 'edge'
  }
  
  return 'other'
}

export function isMobile(): boolean {
  if (typeof window === 'undefined') return false
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
}

export function isIOS(): boolean {
  if (typeof window === 'undefined') return false
  return /iPad|iPhone|iPod/.test(navigator.userAgent)
}

export function isAndroid(): boolean {
  if (typeof window === 'undefined') return false
  return /Android/.test(navigator.userAgent)
}

/**
 * Formats artist name as "FirstName L." or just "FirstName" if no lastName
 */
export function formatArtistDisplayName(artist?: Artist | { firstName?: string; lastName?: string }): string | null {
  if (!artist) return null
  
  const { firstName, lastName } = artist
  
  if (firstName && lastName) {
    return `${firstName} ${lastName.charAt(0)}.`
  }
  
  if (firstName) {
    return firstName
  }
  
  return null
}

/**

 * Masks the first half of a string with asterisks
 * e.g. "johndoe" becomes "****doe"
 */
export function maskFirstHalf(str: string): string {
  if (!str || str.length <= 2) return str
  const halfLength = Math.ceil(str.length / 2)
  const masked = '*'.repeat(halfLength)
  return masked + str.slice(halfLength)
}
